import type { EditFieldSpec, FieldGroupConfig } from './edit-field-group-modal';

/** A field group's record, as the API returns it. */
export type FieldGroupEntity = Record<string, unknown>;
/** The form's values: one per field, plus `dx_reason`. */
export type FieldGroupValues = Record<string, string | boolean>;

/** A field's starting form value: checkboxes as booleans, everything else as text. */
export function initialValue(field: EditFieldSpec, entity: FieldGroupEntity): string | boolean {
  const raw = entity[field.name];
  if (field.type === 'checkbox') return Boolean(raw);
  return raw == null ? '' : String(raw);
}

/** The form's starting values for a record. */
export function buildDefaults(config: FieldGroupConfig, entity: FieldGroupEntity): FieldGroupValues {
  const v: FieldGroupValues = { dx_reason: '' };
  for (const f of config.fields) v[f.name] = initialValue(f, entity);
  return v;
}

/**
 * Convert a form value to the patch value for its field. Empty text/select
 * sends '' (a valid value for any varchar column, NOT NULL or nullable);
 * only date/numeric send NULL when cleared, since those column types can't
 * hold ''. Keeps writes from violating NOT NULL on the Exigo custom fields.
 */
function patchValue(field: EditFieldSpec, value: string | boolean): string | number | boolean | null {
  if (field.type === 'checkbox') return Boolean(value);
  const s = String(value);
  if (field.boolean) return s === '' ? null : s === 'true';
  if (field.numeric) return s === '' ? null : Number(s);
  if (field.type === 'date') return s === '' ? null : s;
  return s; // text / select: '' is a valid varchar value
}

/**
 * The `patch` a field-group save sends, or null when an edit changed nothing.
 *
 * - **Read-only fields are never sent** (#915), in either mode. They are
 *   displayed for context only; sending one would trip `validateCustomerPatch`
 *   (engine-owned and `customer.minimum` columns are un-editable server-side)
 *   and log a change for a column the operator never touched. A masked field
 *   is read-only (`applyAccess`).
 * - **An edit sends only the fields the operator changed**, compared with the
 *   record's own values. An untouched field is not sent, so saving one field
 *   never rewrites (and change-logs) another, and a stored value with stray
 *   spaces never trips the API's refusal of padded strings.
 * - **Create sends every editable field** (#1026): there is no record to
 *   compare with.
 * - An edited text value is trimmed. Selects are not: their value is an
 *   option's, not something typed.
 */
export function buildFieldGroupPatch(
  config: FieldGroupConfig,
  entity: FieldGroupEntity,
  values: FieldGroupValues,
  isCreate: boolean,
): Record<string, unknown> | null {
  const initial = buildDefaults(config, entity);
  const patch: Record<string, unknown> = {};
  for (const f of config.fields) {
    if (f.readOnly) continue;
    const value = values[f.name] ?? '';
    if (!isCreate && value === initial[f.name]) continue;
    patch[f.name] = patchValue(f, f.type === 'text' && typeof value === 'string' ? value.trim() : value);
  }
  return !isCreate && Object.keys(patch).length === 0 ? null : patch;
}
